// The reel STAGE + shared background helpers.
//   • VideoBeatCanvas — the ACTIVE beat as the live editable canvas (its text-free background
//     behind the editable overlay). Mounts in the composer CENTER (the platform preview).
//   • BeatBackgroundFill / ROLE_LABEL / BG_LABEL — shared by the bottom timeline (ReelTimeline).
//
// The bottom, timecoded multi-lane timeline lives in ReelTimeline.tsx. UNLIKE the carousel/static
// editors, edits NEVER auto-render — they save live; the reel is rebuilt only on explicit Render.
// See CompositionEditorProvider's VideoProviderInner + video-ops.

import { useEffect, useRef } from 'react'
import { cn } from 'lib/utils'
import { useIsMobile } from 'hooks/use-mobile'
import { fillToCss } from '../render-helpers'
import { CompositionCanvas } from '../CompositionCanvas'
import type { BeatBackground, ImageSrc, PostComposition, VideoBeatComposition } from '../types'
import { clipsInWindow } from '../video-time'
import { useAttachmentUrl } from '../use-attachment-url'
import { CanvasMediaWithBar } from './ComposerCanvasMedia'
import {
  useVideoTimeline,
  type VideoTimelineContextValue,
  useReelPlayhead,
} from './CompositionEditorProvider'
import { backgroundPosterSrc, directSrcUrl } from './video-bg'

/** A beat's text-free background as a still (card → its Fill; photo → the photo; video → a real
 *  FRAME of the clip). A video background usually has no separate image poster — its "poster" IS
 *  the clip mp4, which an `<img>` cannot paint (the black-tile bug) — so clips render as a paused
 *  `<video>` pinned to an early frame (`#t=0.1` makes every browser actually paint it). */
export function BeatBackgroundFill({ bg, stage }: { bg: BeatBackground; stage?: boolean }) {
  const posterSrc = bg.kind === 'card' ? undefined : backgroundPosterSrc(bg)
  const attachmentId = posterSrc?.kind === 'attachment' ? posterSrc.attachmentId : undefined
  const resolved = useAttachmentUrl(attachmentId)
  const url = directSrcUrl(posterSrc) ?? resolved
  if (bg.kind === 'card') {
    return <div className="absolute inset-0" style={{ background: fillToCss(bg.background) }} />
  }
  // Blueprint PLANNED scene: no clip yet — show the scene intent as a storyboard card
  // instead of a black tile. On the STAGE (the big preview) it reads as a full storyboard
  // frame: dashed frame + Blueprint badge + the scene direction at reading size, so it's
  // unmistakably the plan for the video about to be created. Timeline chips keep the
  // compact version.
  if (bg.kind === 'video' && bg.planned === true && !bg.clipSrc && !url) {
    if (stage) {
      return (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-[linear-gradient(160deg,#15151a_0%,#0B0B0C_100%)] px-[10%] text-center">
          <div
            className="pointer-events-none absolute inset-4 rounded-2xl border border-dashed border-white/15"
            aria-hidden
          />
          <span aria-hidden className="text-[44px] leading-none">
            🎬
          </span>
          <span className="mt-4 rounded-full border border-white/20 px-3 py-1 text-[11px] font-semibold tracking-[0.18em] text-white/70 uppercase">
            Blueprint · scene not filmed yet
          </span>
          {bg.sceneIdea && (
            <span className="mt-4 line-clamp-6 max-w-[420px] text-[16px] leading-relaxed font-medium text-paper">
              {bg.sceneIdea}
            </span>
          )}
          <span className="mt-4 text-[11px] text-paper-faint">
            Filmed when you bring the reel to life · or tap Film this scene in the inspector
          </span>
        </div>
      )
    }
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-[linear-gradient(160deg,#15151a_0%,#0B0B0C_100%)] px-[8%] text-center">
        <span aria-hidden className="text-[16px] leading-none opacity-80">
          🎬
        </span>
        {bg.sceneIdea && (
          <span className="line-clamp-3 text-[8px] leading-snug text-paper-faint">
            {bg.sceneIdea}
          </span>
        )}
      </div>
    )
  }
  const isClip = bg.kind === 'video' && posterSrc !== bg.posterSrc
  // Photo-first direction parity (I14): the master cover-crops AROUND the focal point and
  // punches in, so the preview positions the photo on the focal point and applies a subtle
  // static scale per punch level ('hard' approximates the ~55% tight crop). Best-effort CSS —
  // the burned zoom PUSH itself is render-only.
  const photoStyle =
    bg.kind === 'photo'
      ? {
          objectPosition: `${(bg.focalX ?? 0.5) * 100}% ${(bg.focalY ?? 0.5) * 100}%`,
          ...(bg.punch
            ? {
                transform: `scale(${bg.punch === 'hard' ? 1.8 : bg.punch === 'push' ? 1.05 : 1.02})`,
                transformOrigin: `${(bg.focalX ?? 0.5) * 100}% ${(bg.focalY ?? 0.5) * 100}%`,
              }
            : {}),
        }
      : undefined
  return (
    <div className="absolute inset-0 overflow-hidden bg-black">
      {url ? (
        isClip ? (
          <video
            src={`${url}#t=0.1`}
            muted
            playsInline
            preload="metadata"
            className="h-full w-full object-cover"
          />
        ) : (
          <img
            src={url}
            alt=""
            className="h-full w-full object-cover"
            style={photoStyle}
            draggable={false}
          />
        )
      ) : null}
    </div>
  )
}

// ───────────────────────── Center: the active beat canvas (stage) ─────────────────────────

/** The stage — mounts in the composer center (the preview).
 *  • Paused: the ACTIVE beat as the live EDITABLE canvas (text-free background behind the overlay
 *    editor; every per-beat text tool works unchanged).
 *  • Playing (K2): a read-only playback view of the beat under the playhead — background clip loops
 *    (or poster/Ken-Burns), the overlay is composited on top, the current word-timed caption shows,
 *    and VO/music play when their srcs exist. Advancing the playhead is the provider's rAF clock. */
/** The PLAYBACK doc for a beat: every clip intersecting the beat's effective span, each with
 *  a synthesized beat-relative `timing` (its window shifted by the span start, un-clamped) so
 *  CompositionCanvas's beatTime gate + anim styles preview windows/entrances exactly. */
function playbackDoc(timeline: VideoTimelineContextValue, index: number): PostComposition {
  const span = timeline.spans[index] ?? { start: 0, end: 0 }
  const beat = timeline.beats[index]!
  const layers = clipsInWindow(timeline.clips, span.start, span.end).map(c => ({
    ...c.layer,
    timing: { start: c.start - span.start, end: c.end - span.start },
  }))
  return { ...beat.overlay, layers, meta: { ...beat.overlay.meta, dirty: false } }
}

export function VideoBeatCanvas({ onTap }: { onTap?: () => void }) {
  const timeline = useVideoTimeline()
  const isMobile = useIsMobile()
  if (!timeline) return null
  if (timeline.playing) return <ReelPlaybackStage />
  const activeBeat = timeline.beats[timeline.activeIndex]
  const mm = Math.floor(timeline.totalDuration / 60)
  const ss = String(Math.round(timeline.totalDuration % 60)).padStart(2, '0')
  return (
    <div className="relative w-full overflow-hidden rounded-[18px] bg-black shadow-2xl">
      {activeBeat && <BeatBackgroundFill bg={activeBeat.background} stage />}
      {/* The editable canvas IS the active-beat view: every clip whose window touches this
          beat (including reel-spanning ones) is a directly-editable layer here. */}
      <div className="relative">
        <CanvasMediaWithBar clipClassName="w-full overflow-hidden" onTap={onTap} />
      </div>
      {/* MOBILE keeps a floating preview transport ON the stage: the bottom timeline dock
          (which owns play on desktop) doesn't mount there, so without this pill playback
          would be unreachable on a phone. */}
      {isMobile && (
        <button
          type="button"
          onClick={() => timeline.play()}
          title="Live preview · Render for the final cut"
          aria-label="Play the live preview"
          className="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-white/25 bg-black/60 px-3.5 py-1.5 text-[12px] font-semibold text-white backdrop-blur-sm transition-colors hover:bg-black/80"
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M8 5v14l11-7z" />
          </svg>
          Preview · {mm}:{ss}
        </button>
      )}
    </div>
  )
}

/** The playhead → which beat + how far into it, on EFFECTIVE spans (during a crossfade
 *  overlap the INCOMING beat owns the playhead — exactly the container's xfade timing). */
function beatAtTime(
  timeline: VideoTimelineContextValue,
  t: number
): { index: number; local: number } {
  const spans = timeline.spans
  for (let i = spans.length - 1; i >= 0; i--) {
    if (t >= spans[i]!.start - 1e-6) return { index: i, local: Math.max(0, t - spans[i]!.start) }
  }
  return { index: 0, local: 0 }
}

/** The current word-timed caption for a beat at a local offset (captions are beat-relative). */
function captionAt(beat: VideoBeatComposition, local: number): string | null {
  const caps = beat.captions ?? []
  for (const c of caps) if (local >= c.start - 1e-6 && local < c.end) return c.text
  return null
}

export function ReelPlaybackStage() {
  const timeline = useVideoTimeline()!
  // The live playback frame is THE per-tick subscriber: captions, crossfade opacity and
  // the clip windows all track the playhead here (and only here).
  const playhead = useReelPlayhead(timeline)
  const { index, local } = beatAtTime(timeline, playhead)
  const beat = timeline.beats[index]
  if (!beat) return null
  const caption = captionAt(beat, local)
  // Crossfade preview on the REAL overlap: the spans overlap by the dissolve length, so while
  // the playhead is inside it the PREVIOUS beat's background lingers on top, fading out — the
  // same window the master's xfade burns.
  const overlap =
    index > 0
      ? Math.max(0, (timeline.spans[index - 1]?.end ?? 0) - (timeline.spans[index]?.start ?? 0))
      : 0
  const prevBeat = overlap > 0 && local < overlap ? timeline.beats[index - 1] : undefined
  const prevOpacity = prevBeat ? 1 - local / overlap : 0
  return (
    <div
      className="relative mx-auto aspect-[9/16] w-full max-w-[400px] overflow-hidden rounded-[18px] bg-black shadow-2xl"
      onClick={() => timeline.pause()}
      role="button"
      aria-label="Pause playback"
    >
      <BeatPlaybackBackground key={beat.id} bg={beat.background} startAt={local} />
      {prevBeat && (
        <div className="pointer-events-none absolute inset-0" style={{ opacity: prevOpacity }}>
          {/* The lingering side of the dissolve shows the previous clip's ENDING (where it
              just was), not its opening frames — matching the master's xfade. */}
          <BeatPlaybackBackground
            key={`xf-${prevBeat.id}`}
            bg={prevBeat.background}
            startAt={Math.max(0, prevBeat.durationSec - overlap)}
          />
        </div>
      )}
      {/* Every clip active at the playhead — windows + entrances via the synthesized timing. */}
      <div className="pointer-events-none absolute inset-0">
        <CompositionCanvas
          composition={playbackDoc(timeline, index)}
          beatTime={local}
          transparent
        />
      </div>
      {timeline.captionsEnabled && timeline.voEnabled && caption && (
        <div
          className={cn(
            'pointer-events-none absolute inset-x-0 flex justify-center px-6',
            timeline.captionPlacement === 'top'
              ? 'top-[12%]'
              : timeline.captionPlacement === 'center'
                ? 'top-1/2 -translate-y-1/2'
                : 'bottom-[12%]'
          )}
        >
          <span
            className={cn(
              'rounded-md text-center leading-tight',
              timeline.captionStyle === 'clean'
                ? 'bg-black/50 px-2 py-0.5 text-[12px] font-semibold text-white'
                : timeline.captionStyle === 'block'
                  ? 'px-2.5 py-1 text-[14px] font-bold tracking-tight uppercase'
                  : 'bg-black/70 px-2.5 py-1 text-[15px] font-bold tracking-tight text-white uppercase'
            )}
            style={
              timeline.captionStyle === 'block'
                ? { background: beat.overlay.brand.palette.accent ?? '#C6FF3D', color: '#0B0B0C' }
                : undefined
            }
          >
            {caption}
          </span>
        </div>
      )}
      {timeline.voEnabled && <ReelBeatAudio beat={beat} startAt={local} />}
      {timeline.musicEnabled && timeline.musicSrc && (
        <ReelMusicBed src={timeline.musicSrc} startAt={playhead} />
      )}
    </div>
  )
}

/** A beat's background during playback: a looping clip when we have a playable mp4, else the poster
 *  still (photo gets a subtle Ken-Burns push-in, card is a flat fill). `startAt` seeks the clip
 *  once on load (mount-time offset — a mid-beat play or a dissolve's lingering tail starts at
 *  the right frame; the element keeps its own clock after that). */
function BeatPlaybackBackground({ bg, startAt = 0 }: { bg: BeatBackground; startAt?: number }) {
  const clipSrc: ImageSrc | undefined =
    bg.kind === 'video' ? (bg.clipSrc ?? bg.candidates?.[0]) : undefined
  const clipAttId = clipSrc?.kind === 'attachment' ? clipSrc.attachmentId : undefined
  const clipResolved = useAttachmentUrl(clipAttId)
  const clipUrl = directSrcUrl(clipSrc) ?? clipResolved
  const startAtRef = useRef(startAt) // mount-time only; never re-seek on prop churn
  if (bg.kind === 'video' && clipUrl) {
    return (
      <video
        src={clipUrl}
        className="absolute inset-0 h-full w-full object-cover"
        autoPlay
        muted
        loop
        playsInline
        onLoadedMetadata={e => {
          const t = startAtRef.current
          if (t <= 0.05) return
          try {
            const el = e.currentTarget
            el.currentTime = Number.isFinite(el.duration) && el.duration > 0 ? t % el.duration : t
          } catch {
            /* not seekable — plays from 0 */
          }
        }}
      />
    )
  }
  // Photo / card (or a video whose clip URL didn't resolve) → the poster still.
  return <BeatBackgroundFill bg={bg} stage />
}

/** Plays a beat's voiceover (when its src exists), SEEKED to the beat-local playhead so a
 *  mid-beat play stays in sync with the word-timed captions; remounts per beat via key.
 *  Voices land pre-render via VO-on-demand (P1); a beat still missing its take plays silent. */
function ReelBeatAudio({ beat, startAt }: { beat: VideoBeatComposition; startAt: number }) {
  const attId = beat.voSrc?.kind === 'attachment' ? beat.voSrc.attachmentId : undefined
  const resolved = useAttachmentUrl(attId)
  const url = directSrcUrl(beat.voSrc ?? undefined) ?? resolved
  const ref = useRef<HTMLAudioElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el || startAt <= 0.05) return
    try {
      el.currentTime = startAt
    } catch {
      /* not seekable yet — plays from 0 */
    }
    // Mount-time only: the element keeps its own clock while the beat plays.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url])
  if (!url) return null
  return <audio ref={ref} key={beat.id} src={url} autoPlay />
}

/** The reel's locked music bed, DUCKED under the voiceover (P2). Mounts once per playback run,
 *  seeks to the playhead so a mid-reel play stays in sync, and loops (the final mix trims). */
function ReelMusicBed({ src, startAt }: { src: ImageSrc; startAt: number }) {
  const attId = src.kind === 'attachment' ? src.attachmentId : undefined
  const resolved = useAttachmentUrl(attId)
  const url = directSrcUrl(src) ?? resolved
  const ref = useRef<HTMLAudioElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.volume = 0.22
    try {
      el.currentTime = startAt
    } catch {
      /* not seekable yet — starts at 0, close enough for a preview */
    }
    // startAt is mount-time only by design: the element keeps its own clock while playing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url])
  if (!url) return null
  return <audio ref={ref} src={url} autoPlay loop />
}
